import * as React from "react";
import Comments, { IThread, INewPost, PostReference, IPost } from "./Comments";
import { apiGet, apiPost, apiDelete, apiPut } from "../../services/api";
import { useSnackbar } from "notistack";

export interface TopicContainerProps {
    projectId: number;
    topic: string;

    onPostMessage?(msg: INewPost): Promise<void>;

    applyBookmark?(bookmark: string): Promise<void>;
    clearBookmark(): Promise<void>;
}

export const TopicContainer: React.FC<TopicContainerProps> = props => {
    const [threads, setThreads] = React.useState<IThread[]>([]);
    const [isLoading, setisLoading] = React.useState(true);

    const { enqueueSnackbar } = useSnackbar();

    const refreshThreads = async () => {
        const topicThreads = (await apiGet(
            `comments/${props.projectId}/${props.topic}`
        )) as IThread[];

        setThreads(topicThreads);
    };

    const createNewThread = async (post: INewPost) => {
        if (props.onPostMessage) {
            await props.onPostMessage(post);
        }
        try {
            const newThread = (await apiPost(
                `comments/${props.projectId}/${props.topic}`,
                post
            )) as IThread;

            setThreads(existingThreads => [newThread, ...existingThreads]);
        } catch {
            enqueueSnackbar(`Unable to post comment. Please try again later.`, {
                variant: "error"
            });
        }
    };

    const deletePost = async (post: PostReference) => {
        await apiDelete(
            `comments/${props.projectId}/${post.topic}/${post.thread}/${post.id}`
        );

        const thread = threads.find(a => a.id === post.thread);

        if (thread?.posts[0].id === post.id) {
            setThreads(threads.filter(a => a.id !== post.thread));

            return false;
        }

        return true;
    };

    const toggleFavorite = async (post: PostReference) => {
        const result = (await apiPut(
            `comments/${props.projectId}/${post.topic}/${post.thread}/${post.id}/toggle-favorite`
        )) as IPost;

        return result.favorited;
    };

    const applyBookmark = async (bookmark: string) => {
        if (props.applyBookmark) {
            await props.applyBookmark(bookmark);
        }
    };

    React.useEffect(() => {
        const loadThreads = async () => {
            await refreshThreads();

            setisLoading(false);
        };

        loadThreads();
    }, []);

    const refreshComments = async () => {
        setisLoading(true);
        setThreads([]);
        await refreshThreads();

        setisLoading(false);
    };

    const clearBookmark = async () => {
        if (props.clearBookmark) {
            await props.clearBookmark();
        }
    };

    return (
        <Comments
            projectId={props.projectId}
            deletePost={deletePost}
            threads={threads}
            refreshComments={refreshComments}
            clearBookmark={clearBookmark}
            onPostMessage={createNewThread}
            applyBookmark={applyBookmark}
            toggleFavorite={toggleFavorite}
            isLoading={isLoading}
            topic={props.topic}
        />
    );
};

export default TopicContainer;
